import { Injectable, inject } from '@angular/core';
import { DataDictionary } from '../../model/data-dictionary';
import { HisBackendService } from '../backend/his-backend.service';
import { HisBaseGlobalsService } from './his-base-globals.service';
import { HisDdDirectiveRegistryService } from '../directive/his-dd-directive-registry.service';

/**
 * Reloads the data dictionary from the backend and re-applies it to all currently registered form
 * fields after a data dictionary configuration has been changed in a popup window (e.g. opened by
 * HisDdPopupComponent). Lives in a root service (and not in a component), so the watching survives
 * any re-rendering or destruction of the opening component.
 */
@Injectable({providedIn: 'root'})
export class HisDdRefreshService {
    private hisBackendService = inject( HisBackendService );
    private hisBaseGlobalsService = inject( HisBaseGlobalsService );
    private ddDirectiveRegistryService = inject( HisDdDirectiveRegistryService );

    private openedPopupWindows: Window[] = [];
    private closeCheckTimer: number | null = null;

    /**
     * Starts watching the given popup window (opened by a data dictionary edit flow). As soon as
     * all watched popups have been closed, the data dictionary is reloaded and re-applied to the
     * current form fields, so that newly created or changed configurations take effect without a
     * full page reload.
     */
    watchForDdRefresh( popupWindow: Window | null ): void {
        if ( !popupWindow ) {
            return; // popup was blocked by the browser
        }
        console.log( 'HisDdRefreshService: watching popup window for closing' );
        this.openedPopupWindows.push( popupWindow );
        if ( this.closeCheckTimer !== null ) {
            return;
        }
        this.closeCheckTimer = window.setInterval( this.checkForClosedPopups, 1000 );
        // timer callbacks are throttled in background tabs; the focus listener makes sure
        // the closing is detected as soon as the user returns to this window
        window.addEventListener( 'focus', this.checkForClosedPopups );
    }

    private checkForClosedPopups = (): void => {
        if ( this.openedPopupWindows.length === 0 ) {
            return;
        }
        this.openedPopupWindows = this.openedPopupWindows.filter( ( popupWindow ) => {
            let isClosed = false;
            try {
                isClosed = popupWindow.closed;
            } catch {
                // window reference not accessible, keep watching
            }
            return !isClosed;
        } );
        if ( this.openedPopupWindows.length === 0 ) {
            this.stopWatchingPopups();
            // small delay to give a still pending save request of the popup some time to finish
            setTimeout( () => this.refreshDataDictionary(), 500 );
        }
    };

    private stopWatchingPopups(): void {
        if ( this.closeCheckTimer !== null ) {
            clearInterval( this.closeCheckTimer );
            this.closeCheckTimer = null;
        }
        window.removeEventListener( 'focus', this.checkForClosedPopups );
    }

    /**
     * Reloads the data dictionary from the backend and re-applies it to all currently registered
     * form fields, so that newly created or changed configurations are displayed and enforced.
     */
    refreshDataDictionary(): void {
        console.log( 'HisDdRefreshService: refreshing data dictionary' );
        this.hisBackendService.getData( 'api/v1/cs/sys/datadictionary/allContexts' ).subscribe( ( dd: DataDictionary ) => {
            if ( dd && Object.keys( dd ).length !== 0 ) {
                this.hisBaseGlobalsService.setDd( dd );
                console.log( 'HisDdRefreshService: data dictionary reloaded, re-applying to',
                    this.ddDirectiveRegistryService.getHisDdRegistryDdDirectives().length, 'registered field(s)' );
                this.ddDirectiveRegistryService.getHisDdRegistryDdDirectives().forEach( ( ddDirective ) => {
                    ddDirective.notifyDdKeyOrContextsChanged();
                } );
            }
        } );
    }
}
